import _find from "lodash/find"
import _map from "lodash/map"

import getGlobalSiteSettings from "@src/pages/api/get-global-site-settings"
import getPaths from "@src/pages/api/get-faq-paths"

import apolloClient from "@src/utilities/apollo-client"
import convertFaqUuidsToFaqItems from "@src/utilities/convert-faq-uuids-to-faq-items"
import { GET_FAQ_CATEGORIES_PATHS } from "@src/utilities/graphql-storyblok-queries"
import Storyblok from "@src/utilities/storyblok"
import getStoryblokApiKey from "@src/utilities/storyblok-api-key"

import getStoryblokCacheVersion from "@src/workers/storyblok/get-cache-version"

import useStoryblokBridge from "@src/hooks/use-storyblok-bridge"

import GeneralLayout from "@src/layouts/general"

import CategoryFilter from "@src/components/CategoryFilter"
import PageBuilder from "@src/components/page-builder"
import SEO from "@src/components/seo"

import type { GetStaticPropsContext, InferGetStaticPropsType } from "next"
import type { GetFaqCategoriesPathsQuery } from "@src/entities/types/storyblok"

export default function FAQPage({
  categories,
  preview,
  selectedCategory,
  siteSettings,
  story,
  storyblokApiKey,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  let blok = useStoryblokBridge(story, storyblokApiKey, preview)
  blok = {
    ...blok,
    content: {
      ...blok.content,
      faq_list: story.content.faq_list,
    },
  }

  return (
    <GeneralLayout siteSettings={siteSettings}>
      {blok.content && blok.content.seo && <SEO {...blok.content.seo} />}
      <CategoryFilter
        links={categories ?? []}
        selectedLink={selectedCategory}
        viewAllPath="/faqs"
      />
      <PageBuilder content={blok?.content} />
    </GeneralLayout>
  )
}

export const getStaticPaths = async () => {
  const paths = await getPaths()
  return {
    paths: _map(["", ...paths], (slug) => ({
      params: {
        slug: [slug],
      },
    })),
    fallback: false,
  }
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const slug = (context.params && (context.params.slug as string[])) ?? [""]
  const apiKey = getStoryblokApiKey(context.preview)
  const cacheVersion = await getStoryblokCacheVersion()

  const [siteSettings, storyRequest, faqCategories] = await Promise.all([
    getGlobalSiteSettings(context.preview ?? false),
    Storyblok(apiKey).get(`cdn/stories/faqs/${slug.join("/")}`, {
      cv: cacheVersion,
      version: context.preview ? "draft" : "published",
    }),
    apolloClient.query<GetFaqCategoriesPathsQuery>({
      context: {
        clientName: "storyblok",
        headers: {
          Token: apiKey,
          Version: cacheVersion,
        },
      },
      query: GET_FAQ_CATEGORIES_PATHS,
    }),
  ])

  const story = await convertFaqUuidsToFaqItems(
    storyRequest.data.story,
    context.preview
  )

  const categories = _map(
    faqCategories.data.FaqpageItems?.items ?? [],
    (item) => ({
      name: item?.content?.title ?? "FAQs",
      path: `/${item?.full_slug}`,
    })
  )
  const selectedCategory = _find(
    categories,
    (category) => category.path === `/faqs/${slug}`
  )

  return {
    props: {
      cartCount: 0,
      categories,
      preview: context.preview ?? false,
      ...(selectedCategory && { selectedCategory }),
      sessionToken: "",
      siteSettings,
      story,
      ...(context.preview && { storyblokApiKey: apiKey }),
    },
  }
}
